/**
 * Bulk Actions V2 - Vuex Store Module
 *
 * Handles bulk operations for CollectionItem resources (bookmarks, notes, and future types).
 * Uses excludeItemIds instead of excludeBookmarkIds for the selection mechanism.
 */
import CollectionItem from '@/store/models/collectionItems/CollectionItem'
import Collection from '@/store/models/collections/Collection'
import CollectionMember from '@/store/models/collections/CollectionMember'
import CollectionShareLink from '@/store/models/collections/CollectionShareLink'
import { stateGetters, stateSetters } from '@/utils/store/store-utils'
import {
  DEFAULT_DURATION,
  MAX_READ_TIME,
  DEACTIVATION_DURATION,
} from '@/utils/constants'
import ResourceTag from '@/store/models/collectionItems/ResourceTag'

const defaultState = {
  isActive: false,
  isAllSelected: false,
  publicSourceContext: null,
  selection: {
    isAllSelected: false, // TODO: remove this (legacy)
    excludeItemIds: [],
    excludeCollectionIds: [],
  },
  tagFilters: {
    next: null,
    asc: false,
    sortBy: 'createdAt',
    showCount: 25,
  },
}

export default {
  name: 'bulkActionsV2',
  namespaced: true,
  state: defaultState,
  getters: {
    ...stateGetters(defaultState),

    /**
     * Build filter payload matching collectionItems store filters
     */
    filterPayload(_s, _g, _rs, rootGetters) {
      const filters = rootGetters['collectionItems/filters']
      const isCollection = rootGetters['collectionItems/isCollection']

      const search = filters.search?.trim()
      const isFavorite = !isCollection && filters.isFavorite
      const readTime = [
        filters.readTime[0] || false,
        filters.readTime[1] >= MAX_READ_TIME ? false : filters.readTime[1],
      ]

      return {
        filters: {
          tags: filters.tags,
          ...(filters.resourceType && { resourceType: filters.resourceType }),
          ...(isFavorite && { isFavorite }),
          ...(search?.length >= 2 && { search }),
          ...(readTime.some(Boolean) && { readTime }),
          ...(filters.languages.length && { languages: filters.languages }),
        },
      }
    },

    /**
     * Check if there are active filters
     */
    hasActiveFilters(_s, getters) {
      const { filterPayload } = getters
      if (Object.keys(filterPayload.filters).length > 1) return true
      if (filterPayload.filters.tags && filterPayload.filters.tags.length > 0) return true
      return false
    },

    /**
     * Get selected items based on selection state
     */
    items(state, getters, _rs, rootGetters) {
      const collectionId = rootGetters['collectionItems/collectionId']

      if (state.isAllSelected) {
        // Return all items except those in excludeItemIds
        const allItems = CollectionItem.query()
          .where(record => record.ownerId === collectionId)
          .get()
        return allItems.filter(item => !state.selection.excludeItemIds.includes(item.id))
      }
      // Return only items that are in excludeItemIds (inverted logic when not all selected)
      return CollectionItem.query()
        .where(record => record.ownerId === collectionId)
        .where(record => state.selection.excludeItemIds.includes(record.id))
        .get()
    },

    /**
     * Get selected collections (for collection bulk actions)
     */
    collections(state) {
      if (state.isAllSelected) {
        const allCollections = Collection.all()
        return allCollections.filter(collection => !state.selection.excludeCollectionIds.includes(collection.id))
      }
      return Collection.query()
        .where(record => state.selection.excludeCollectionIds.includes(record.id))
        .with('shareLink')
        .get()
    },

    /**
     * Get owned collections from selection
     */
    ownedCollections(state, getters, _rs, rootGetters) {
      const selectedCollections = getters.collections
      return selectedCollections.filter(collection => collection.ownerId === rootGetters['auth/userId'])
    },

    /**
     * Get not owned collections from selection
     */
    notOwnedCollections(state, getters, _rs, rootGetters) {
      const selectedCollections = getters.collections
      return selectedCollections.filter(collection => collection.ownerId !== rootGetters['auth/userId'])
    },

    /**
     * Count of selected collections
     *
     * Sums server-derived totals for pinned + non-pinned (the Collections view
     * renders both in the same list), so the count is correct from first paint
     * before pagination has fetched every page. Mirrors how `numberOfItems`
     * uses `collectionItems/filteredItemCount`.
     */
    numberOfCollections(state, _g, _rs, rootGetters) {
      if (state.isAllSelected) {
        const total = rootGetters['collections/totalCount'] + rootGetters['collections/pinnedTotalCount']
        return Math.max(0, total - state.selection.excludeCollectionIds.length)
      }
      return state.selection.excludeCollectionIds.length
    },

    /**
     * Count of selected items
     */
    numberOfItems(state, getters, _rs, rootGetters) {
      if (state.isAllSelected) {
        return Math.max(0, rootGetters['collectionItems/filteredItemCount'] - state.selection.excludeItemIds.length)
      }
      return state.selection.excludeItemIds.length
    },

    /**
     * Total number of selected items (items or collections based on context)
     */
    numberOfSelectedItems(_, g, _rs, rootGetters) {
      if (rootGetters['collectionItems/isCollection']) {
        return g.numberOfCollections
      }
      return g.numberOfItems
    },

    /**
     * Whether to show bulk action bar
     */
    showActions(_, getters) {
      return getters.numberOfItems > 0 || getters.numberOfCollections > 0
    },

    /**
     * Whether the "select all" checkbox is checked
     */
    isCheckboxChecked(state) {
      return state.isAllSelected &&
             state.selection.excludeItemIds.length === 0 &&
             state.selection.excludeCollectionIds.length === 0
    },

    publicSourceContext(state) {
      return state.publicSourceContext
    },
  },

  mutations: {
    ...stateSetters(defaultState),

    /**
     * Add item to selection
     */
    addItem(state, itemId) {
      if (state.isAllSelected) {
        // Remove from exceptions (it's now selected)
        const index = state.selection.excludeItemIds.indexOf(itemId)
        if (index > -1) {
          state.selection.excludeItemIds.splice(index, 1)
        }
      } else {
        // Add to exceptions (it's now selected)
        if (!state.selection.excludeItemIds.includes(itemId)) {
          state.selection.excludeItemIds.push(itemId)
        }
      }
    },

    /**
     * Remove item from selection
     */
    removeItem(state, itemId) {
      if (state.isAllSelected) {
        // Add to exceptions (it's now unselected)
        if (!state.selection.excludeItemIds.includes(itemId)) {
          state.selection.excludeItemIds.push(itemId)
        }
      } else {
        // Remove from exceptions (it's now unselected)
        const index = state.selection.excludeItemIds.indexOf(itemId)
        if (index > -1) {
          state.selection.excludeItemIds.splice(index, 1)
        }
      }
    },

    /**
     * Add collection to selection
     */
    addCollection(state, collectionId) {
      if (state.isAllSelected) {
        const index = state.selection.excludeCollectionIds.indexOf(collectionId)
        if (index > -1) {
          state.selection.excludeCollectionIds.splice(index, 1)
        }
      } else {
        if (!state.selection.excludeCollectionIds.includes(collectionId)) {
          state.selection.excludeCollectionIds.push(collectionId)
        }
      }
    },

    /**
     * Remove collection from selection
     */
    removeCollection(state, collectionId) {
      if (state.isAllSelected) {
        if (!state.selection.excludeCollectionIds.includes(collectionId)) {
          state.selection.excludeCollectionIds.push(collectionId)
        }
      } else {
        const index = state.selection.excludeCollectionIds.indexOf(collectionId)
        if (index > -1) {
          state.selection.excludeCollectionIds.splice(index, 1)
        }
      }
    },

    /**
     * Clear all selections
     */
    clear(state) {
      state.isAllSelected = false
      state.selection.excludeItemIds = []
      state.selection.excludeCollectionIds = []
    },

    /**
     * Close bulk actions and clear selections
     */
    closeActions(state) {
      state.isActive = false
      state.isAllSelected = false
      state.publicSourceContext = null
      state.selection.excludeItemIds = []
      state.selection.excludeCollectionIds = []
    },

    /**
     * Toggle bulk actions mode
     */
    toggleActions(state) {
      state.isActive = !state.isActive
      state.isAllSelected = false
      state.publicSourceContext = null
      state.selection.excludeItemIds = []
      state.selection.excludeCollectionIds = []
    },

    setPublicSourceContext(state, context) {
      state.publicSourceContext = context
    },

    /**
     * Set tags pagination cursor
     */
    setTagsNext(state, newNext) {
      state.tagFilters.next = newNext
    },

    /**
     * Toggle all selected state
     */
    toggleAllSelected(state) {
      const hasExceptions = state.selection.excludeItemIds.length > 0 || state.selection.excludeCollectionIds.length > 0

      if (state.isAllSelected && !hasExceptions) {
        // Everything selected, no exceptions -> toggle to nothing selected
        state.isAllSelected = false
      } else if (state.isAllSelected && hasExceptions) {
        // Everything selected with exceptions -> clear exceptions (select all)
        state.selection.excludeItemIds = []
        state.selection.excludeCollectionIds = []
      } else if (!state.isAllSelected && !hasExceptions) {
        // Nothing selected -> select all
        state.isAllSelected = true
      } else if (!state.isAllSelected && hasExceptions) {
        // Some selected -> select all
        state.isAllSelected = true
        state.selection.excludeItemIds = []
        state.selection.excludeCollectionIds = []
      }
    },
  },

  actions: {
    // ==================== ITEM BULK ACTIONS ====================

    /**
     * Bulk soft delete items
     */
    async delete({ getters, state, rootGetters }) {
      const bulkItems = getters.items
      const bulkItemIds = bulkItems.map(i => i.id)
      const ownerId = rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('remove', true)))

        await this.$api.collectionItem.removeBulk(ownerId, {
          ...getters.filterPayload,
          excludeItemIds: state.selection.excludeItemIds,
          isAllSelected: state.isAllSelected,
        })

        await CollectionItem.delete(record => bulkItemIds.includes(record.id))

        return true
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('remove', false)))
      }
    },

    /**
     * Bulk toggle favorite status
     */
    async toggleFavorite({ getters, rootGetters, state }, { isFavorite }) {
      const bulkItems = getters.items
      const ownerId = rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('favorite', true)))

        const result = await this.$api.collectionItem.favoriteBulk(ownerId, {
          ...getters.filterPayload,
          excludeItemIds: state.selection.excludeItemIds,
          isAllSelected: state.isAllSelected,
          isFavorite,
        })

        bulkItems.forEach(item => {
          CollectionItem.update({
            where: item._id,
            data: { isFavorite },
          })
        })

        return result
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('favorite', false)))
      }
    },

    /**
     * Bulk restore deleted items
     */
    async restore({ getters, rootGetters, state }) {
      const bulkItems = getters.items
      const bulkItemIds = bulkItems.map(i => i.id)
      const ownerId = rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('restore', true)))

        await this.$api.collectionItem.restoreBulk(ownerId, {
          excludeItemIds: state.selection.excludeItemIds,
          isAllSelected: state.isAllSelected,
        })

        await CollectionItem.delete(record => bulkItemIds.includes(record.id))

        return true
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('restore', false)))
      }
    },

    /**
     * Bulk permanent delete items
     */
    async fullDelete({ getters, state, rootGetters }) {
      const bulkItems = getters.items
      const bulkItemIds = bulkItems.map(i => i.id)
      const ownerId = rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('remove', true)))

        await this.$api.collectionItem.removeFullBulk(ownerId, {
          excludeItemIds: state.selection.excludeItemIds,
          isAllSelected: state.isAllSelected,
        })

        await CollectionItem.delete(record => bulkItemIds.includes(record.id))

        return true
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('remove', false)))
      }
    },

    /**
     * Save items to private collection
     */
    async save({ getters, state, rootGetters }, { ...data }) {
      const bulkItems = getters.items
      const privateCollectionId = rootGetters['auth/collectionId']
      const ownerId = rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('save', true)))

        await this.$api.collectionItem.addToCollectionsBulk(ownerId, {
          ...getters.filterPayload,
          excludeItemIds: state.selection.excludeItemIds,
          collectionIds: [privateCollectionId],
          isPrivate: true,
          isAllSelected: state.isAllSelected,
          ...data,
        })

        return true
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('save', false)))
      }
    },

    /**
     * Add items to collections (copy/move)
     */
    async addToCollections({ getters, state, rootGetters }, { collectionIds, ownerId = '', ...data }) {
      const bulkItems = getters.items
      const collection = ownerId || rootGetters['collectionItems/collectionId']

      try {
        await Promise.all(bulkItems.map(item => item.setLoading('save', true)))

        await this.$api.collectionItem.addToCollectionsBulk(collection, {
          ...getters.filterPayload,
          excludeItemIds: state.selection.excludeItemIds,
          collectionIds,
          isAllSelected: state.isAllSelected,
          ...data,
        })

        return true
      } finally {
        await Promise.all(bulkItems.map(item => item.setLoading('save', false)))
      }
    },

    /**
     * Bulk add tags to items
     */
    async addTags({ commit, getters, state }, { tags, ownerId }) {
      const result = await this.$api.collectionItem.addTagsBulk(ownerId, {
        ...getters.filterPayload,
        excludeItemIds: state.selection.excludeItemIds,
        isAllSelected: state.isAllSelected,
        tags,
      })

      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    /**
     * Bulk remove tags from items
     */
    async removeTags({ commit, getters, state }, { tags, ownerId }) {
      const result = await this.$api.collectionItem.removeTagsBulk(ownerId, {
        ...getters.filterPayload,
        excludeItemIds: state.selection.excludeItemIds,
        isAllSelected: state.isAllSelected,
        tags,
      })

      commit('collectionItems/bumpCacheVersion', null, { root: true })
      return result
    },

    // ==================== COLLECTION BULK ACTIONS ====================

    /**
     * Bulk delete collections
     */
    async deleteCollections({ state }, { collections }) {
      await this.$api.collection.removeBulk({
        excludeCollectionIds: state.selection.excludeCollectionIds,
        isAllSelected: state.isAllSelected,
      })

      const collectionIdsToDelete = collections.map(c => c.id)
      await Collection.delete(i => collectionIdsToDelete.includes(i.collectionId))
      await CollectionMember.delete(i => collectionIdsToDelete.includes(i.collectionId))
      await CollectionItem.delete(i => collectionIdsToDelete.includes(i.ownerId))

      return true
    },

    /**
     * Bulk remove member from collections
     */
    async removeMember({ state }, { collections, memberIds }) {
      await this.$api.collection.removeMemberBulk({
        excludeCollectionIds: state.selection.excludeCollectionIds,
        memberIds,
        isAllSelected: state.isAllSelected,
      })

      await CollectionMember.delete(i => memberIds.includes(i.id))
    },

    /**
     * Bulk invite emails to collections
     */
    async inviteEmails({ state }, { emails }) {
      return this.$api.collection.inviteEmailsBulk({
        excludeCollectionIds: state.selection.excludeCollectionIds,
        invites: emails,
        isAllSelected: state.isAllSelected,
      })
    },

    /**
     * Bulk edit share links
     */
    async editShareLinks(_, { tokenMap, duration }) {
      const result = await this.$api.collection.editShareLinkBulk({
        duration,
        links: tokenMap,
      })

      const shareLinkIds = Object.values(tokenMap)

      await Promise.all(shareLinkIds.map(shareLinkId => CollectionShareLink.update({
        where: shareLinkId,
        data: result,
      })))

      return result
    },

    /**
     * Bulk get share links
     */
    async getShareLinks({ state }) {
      const linksMap = await this.$api.collection.shareLinksBulk({
        excludeCollectionIds: state.selection.excludeCollectionIds,
        isAllSelected: state.isAllSelected,
      })

      const shareLinks = Object.values(linksMap).map(link => ({
        ...link,
        collectionId: link.of,
      }))

      await CollectionShareLink.insertOrUpdate({
        data: shareLinks,
      })

      return linksMap
    },

    /**
     * Bulk create share links
     */
    async createShareLinks({ state }) {
      const linkMap = await this.$api.collection.createShareLinkBulk({
        duration: DEFAULT_DURATION,
        excludeCollectionIds: state.selection.excludeCollectionIds,
        isAllSelected: state.isAllSelected,
      })
      return linkMap
    },

    /**
     * Bulk deactivate share links
     */
    async deactivateShareLinks(_, { tokenMap }) {
      const result = await this.$api.collection.editShareLinkBulk({
        duration: DEACTIVATION_DURATION,
        links: tokenMap,
      })

      const shareLinkIds = Object.values(tokenMap)

      await Promise.all(shareLinkIds.map(shareLinkId => CollectionShareLink.update({
        where: shareLinkId,
        data: result,
      })))

      return result
    },

    /**
     * Bulk delete share links
     */
    async deleteShareLinks(_, { tokenMap }) {
      await this.$api.collection.deleteShareLinkBulk({
        links: tokenMap,
      })
      const shareLinkIds = Object.values(tokenMap)
      await CollectionShareLink.delete(i => shareLinkIds.includes(i.id))
    },

    // ==================== TAGS ====================

    /**
     * Get tags for selected items
     */
    async getTags({ getters, state, commit }, ownerId) {
      const { results, next } = await this.$api.collectionItem.getBulkTags(ownerId, {
        ...getters.filterPayload,
        excludeItemIds: state.selection.excludeItemIds,
        isAllSelected: state.isAllSelected,
        next: state.tagFilters.next,
        limit: state.tagFilters.showCount,
        sortBy: state.tagFilters.sortBy,
        asc: state.tagFilters.asc,
      })

      await commit('setTagsNext', next)

      await ResourceTag.insertOrUpdate({ data: results })

      return { results, next }
    },
  },
}
